import { Color } from '@persona-id/design-tokens';
import { UsersCheckIcon } from '@persona-id/design-tokens/icons';
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useFormContext } from 'react-hook-form';
import SystemFields from 'shared/account_types/system_fields.json';

import { OptionType } from 'dashboard/components/ReactSelect';
import {
  Column,
  DEFAULT_FIELD_SCHEMA,
  FieldSchemaEditorTable,
  FieldSchemaPanel,
  fieldSchemasToFormValues,
  FocusReason,
  getAllFieldKeys,
  useFieldSchemaEditor,
} from 'dashboard/components-lib/FieldSchemaEditor';
import { EditorableSchema } from 'dashboard/components-lib/FieldSchemaEditor/constants';
import HeaderActions from 'dashboard/components-lib/FieldSchemaEditor/HeaderActions';
import { isTopLevelField } from 'dashboard/components-lib/FieldSchemaEditor/utils';
import FieldSchemaEditorForm from 'dashboard/components-lib/FieldSchemaEditorForm/FieldSchemaEditorForm';
import IdentifierControls from 'dashboard/components-lib/FieldSchemaEditorForm/panel-additional/IdentifierControls';
import RelationFieldControls from 'dashboard/components-lib/FieldSchemaEditorForm/panel-additional/RelationFieldControls';
import { LabelColumn } from 'dashboard/components-lib/FieldSchemaEditorForm/shared';
import NavConfirmPrompt from 'dashboard/components-lib/NavConfirmPrompt';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { Identifier } from 'dashboard/models/identifier';
import { RIGHT_COLUMN_PORTAL_ID } from 'dashboard/views/DashboardAccountTypesShowView/DashboardAccountTypesShowView';
import OverflowMenu from 'dashboard/views/DashboardTransactionTypesActiveFieldsView/OverflowMenu';
import Card from 'design-system/components/Card';
import { Span } from 'design-system/components/Text';
import { FieldSchema, FieldSchemaType } from 'lib/fields';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import usePermission from 'lib/hooks/usePermission';
import { Permission } from 'lib/permissions';

const SYSTEM_FIELD_KEYS = getAllFieldKeys(SystemFields as FieldSchema[]);
const NESTED_SYSTEM_FIELD_KEYS = SYSTEM_FIELD_KEYS.filter((key) => isTopLevelField(key));

interface AccountTypesFieldTabProps {
  fieldSchemas: EditorableSchema[];
  relationObjectTypeOptions: OptionType<string>[];
  identifiers: Identifier[];
  isUpdatingAccountType: boolean;
  onSaveFieldSchemas: (
    fieldSchemas: EditorableSchema[],
    identifiers: Identifier[],
    deactivateFieldSchemaKeys: string[],
  ) => void;
  showMessage: (message: string, messageType: MessageType, persist?: boolean) => void;
}

const AccountTypesFieldTab = ({
  fieldSchemas,
  identifiers,
  relationObjectTypeOptions,
  isUpdatingAccountType,
  onSaveFieldSchemas,
  showMessage,
}: AccountTypesFieldTabProps) => {
  const [selectedFieldSchemaPath, setSelectedFieldSchemaPath] = useState<string>('');
  const [selectedFieldSchemaReason, setSelectedFieldSchemaReason] = useState<FocusReason>('add');

  const hasWritePermission = usePermission(Permission.AccountTypeWrite);
  const identifierConfigEnabled = useFeatureFlag('dashboard:search-next-frontend-accounts');

  const {
    activeFieldSchemaMap,
    activeFieldSchemas,
    activeIdentifiers,
    formFieldKeys,
    isFieldSchemasDirty,
    isEditingField,
    erroredKeys,
    formState: { isValid },
    deleteDraftField,
    validateAllFieldSchemas,
    updateFieldSchemas,
    addNewFieldSchema,
    getErrorMessage,
    submitSingleFieldSchema,
    editSingleFieldSchema,
    resetSingleFieldSchema,
    resetFormValues,
    updateIdentifiers,
    handleSubmit,
  } = useFieldSchemaEditor({
    defaultValues: {
      fieldSchemaMap: fieldSchemasToFormValues(
        fieldSchemas.filter((type) => !type.config['archived-at']),
      ),
      identifiers,
    },
    mode: 'onChange',
  });

  useEffect(() => {
    resetFormValues({
      fieldSchemaMap: fieldSchemasToFormValues(
        fieldSchemas.filter((type) => !type.config['archived-at']),
      ),
      identifiers,
    });
  }, [fieldSchemas, identifiers, resetFormValues]);

  // todo: verify whether account types are archivable
  const archivedFieldSchemas = useMemo(
    () => fieldSchemas.filter((type) => type.config['archived-at']),
    [fieldSchemas],
  );

  const preexistingFieldKeys = useMemo(() => getAllFieldKeys(fieldSchemas), [fieldSchemas]);

  const selectedFieldSchema = useMemo(() => {
    if (!selectedFieldSchemaPath) {
      return null;
    }

    return activeFieldSchemaMap[selectedFieldSchemaPath]?.fieldSchema;
  }, [activeFieldSchemaMap, selectedFieldSchemaPath]);

  const handleSaveFieldSchema = () => {
    validateAllFieldSchemas();

    if (erroredKeys.length > 0) {
      showMessage('Please fix input errors before saving', MessageType.Failure, false);
      return;
    }

    onSaveFieldSchemas(
      activeFieldSchemas.concat(archivedFieldSchemas) as EditorableSchema[],
      activeIdentifiers || [],
      [],
    );
  };

  const tableIsEditable = useMemo(() => {
    //todo: use a constant here once we make this component more generic (like this...status === AccountTypeStatus.Active && hasWritePermission && !hasUnsavedDrafts;)
    return hasWritePermission && !isEditingField;
  }, [isEditingField, hasWritePermission]);

  const customColumns: Column[] = [
    {
      header: '' /* overflow actions */,
      width: '60px',
      generateContent: (props) => {
        return (
          tableIsEditable && (
            <div style={{ marginLeft: 'auto' }}>
              <OverflowMenu
                fieldSchemas={fieldSchemas}
                draftIdentifiers={activeIdentifiers}
                draftFieldKeys={formFieldKeys}
                includeDeactivateAction={false}
                setInactiveFieldKey={() => true}
                deleteFromActiveFieldSchema={(key: string) => {
                  deleteDraftField(key);
                  setSelectedFieldSchemaPath('');
                  showMessage('Deleted 1 field', MessageType.Success, false);
                }}
                {...props}
              />
            </div>
          )
        );
      },
    },
  ];

  const customFormColumns: Column[] = [
    {
      header: 'Identifier',
      width: '80px',
      generateContent: ({ field }) => {
        return (
          <LabelColumn>
            {field.type !== FieldSchemaType.String
              ? '-'
              : // TODO figure out why decorated identifier not working
                field.identifier != null
                ? 'Yes'
                : 'No'}
          </LabelColumn>
        );
      },
    },
  ];

  const rightPanelElement = document.getElementById(RIGHT_COLUMN_PORTAL_ID);
  const enableFieldSchemaEditorForm = useFeatureFlag('dashboard:enable-field-schema-editor-form');

  return (
    <>
      {enableFieldSchemaEditorForm ? (
        <FieldSchemaEditorForm
          fieldSchemas={fieldSchemas}
          nullState={<Span>This account type doesn&apos;t have any fields configured.</Span>}
          identifiers={identifiers}
          additionalColumns={customFormColumns}
          systemFieldSchemas={SystemFields as FieldSchema[]}
          rightPanelPortalId={RIGHT_COLUMN_PORTAL_ID}
          editable={tableIsEditable}
          isPending={isUpdatingAccountType}
          onSubmit={(data) => {
            const identifiers: Identifier[] = data.fieldSchemas
              .map((fieldSchema) => fieldSchema.identifier)
              .filter((identifier): identifier is Identifier => identifier != null);
            onSaveFieldSchemas(data.fieldSchemas, identifiers, data.deactivateFieldSchemaKeys);
          }}
          canDeactivate={(fieldSchema, isNested) => {
            // Do not allow deactivate on nested schema
            return !isNested && !!fieldSchema.persisted;
          }}
          canDelete={(fieldSchema, _) => {
            return !fieldSchema.persisted;
          }}
          canEdit={(fieldSchema) => !fieldSchema.persisted}
          renderAdditionalPanelContent={(
            fieldSchema: EditorableSchema,
            fieldName: string,
            formContext: ReturnType<typeof useFormContext>,
          ) => (
            <>
              <IdentifierControls
                fieldSchema={fieldSchema}
                fieldName={fieldName}
                formContext={formContext}
                canEdit={(fieldSchema) => !fieldSchema.persisted}
              />
              <RelationFieldControls
                fieldSchema={fieldSchema}
                fieldName={fieldName}
                relationObjectTypeOptions={relationObjectTypeOptions}
                formContext={formContext}
                canEdit={(fieldSchema) => !fieldSchema.persisted}
              />
            </>
          )}
          allowRelation
        />
      ) : (
        <>
          <form onSubmit={handleSubmit(handleSaveFieldSchema)}>
            <Card>
              <NavConfirmPrompt prevent={isFieldSchemasDirty} />
              <Card.Body>
                <Card.Title
                  actions={
                    <HeaderActions
                      canAdd={tableIsEditable}
                      canSave={!isEditingField && isValid && isFieldSchemasDirty}
                      onAdd={() => {
                        const { fieldSchemaPath } = addNewFieldSchema();
                        setSelectedFieldSchemaPath(fieldSchemaPath);
                      }}
                    />
                  }
                >
                  Fields
                </Card.Title>
                <FieldSchemaEditorTable
                  productIcon={<UsersCheckIcon size={48} color={Color.BorderIconSecondary} />}
                  inputSchema={activeFieldSchemas}
                  inputIdentifiers={activeIdentifiers}
                  customColumns={customColumns}
                  focusedKey={selectedFieldSchemaPath}
                  erroredKeys={erroredKeys}
                  uneditableKeys={[...preexistingFieldKeys, ...NESTED_SYSTEM_FIELD_KEYS]}
                  preexistingKeys={preexistingFieldKeys}
                  editable={tableIsEditable}
                  nullState={
                    <Span>This account type doesn&apos;t have any fields configured.</Span>
                  }
                  onUpdateSchema={updateFieldSchemas}
                  updateIdentifiers={updateIdentifiers}
                  onFocusSchema={(path: string, focusReason: FocusReason) => {
                    // Do not allow user to navigate away until they finish editing the current one
                    if (!isEditingField) {
                      setSelectedFieldSchemaPath(path);
                      setSelectedFieldSchemaReason(focusReason);
                    }
                  }}
                  identifierConfigEnabled={identifierConfigEnabled}
                />
              </Card.Body>
            </Card>
          </form>
          {rightPanelElement != null &&
            selectedFieldSchema != null &&
            createPortal(
              <FieldSchemaPanel
                isComputedFieldsEnabled={false}
                allowRelation={true}
                relationObjectTypeOptions={relationObjectTypeOptions}
                fieldSchema={selectedFieldSchema || DEFAULT_FIELD_SCHEMA}
                fieldSchemaPath={selectedFieldSchemaPath}
                isSystemField={SYSTEM_FIELD_KEYS.some((key) => key === selectedFieldSchemaPath)}
                isNewField={!preexistingFieldKeys.includes(selectedFieldSchemaPath)}
                errorMessage={getErrorMessage(selectedFieldSchemaPath)}
                selectReason={selectedFieldSchemaReason}
                identifierConfig={activeIdentifiers?.find(
                  (identifier) => identifier.key === selectedFieldSchema?.key,
                )}
                canConfigureIdentifier={identifierConfigEnabled}
                isTopLevelField={isTopLevelField(selectedFieldSchemaPath)}
                systemFieldSchemas={SystemFields as FieldSchema[]}
                onEditing={(fieldSchema, identifierConfig) => {
                  const { nextFieldSchemaPath } = editSingleFieldSchema(selectedFieldSchemaPath, {
                    fieldSchema,
                    identifierConfig,
                  });
                  setSelectedFieldSchemaPath(nextFieldSchemaPath);
                }}
                onCancel={() => {
                  resetSingleFieldSchema();
                  setSelectedFieldSchemaPath('');
                }}
                onUpdate={(fieldSchema, identifierConfig) => {
                  submitSingleFieldSchema(selectedFieldSchemaPath, {
                    fieldSchema,
                    identifierConfig,
                  });
                  setSelectedFieldSchemaPath('');
                }}
              />,
              rightPanelElement,
            )}
        </>
      )}
    </>
  );
};

export default AccountTypesFieldTab;
